<template>
  <div v-if="!isAnonymized" class="w-full my-2">
    <div :class="['flex flex-row items-center', variant?.product?.type === PRODUCT_TYPE.AUCTION && position === 'inside' ? 'justify-end' : 'justify-between']">
        <template v-if="(variant?.product?.type !== PRODUCT_TYPE.AUCTION && position === 'inside') || (variant?.product?.type === PRODUCT_TYPE.AUCTION && position === 'outside')">
            <span v-if="(auctionPrice || _.get(variant, 'price', null)) === null" class="flex items-center">
                <ins class="text-base font-bold text-primary-dark no-underline">
                {{ $t('text-price-negotiable') }}
                </ins>
            </span>
            <span v-else class="flex items-center">
                <ins class="text-base font-bold text-primary-dark no-underline">
                {{ auctionPriceLabel ? $t(auctionPriceLabel) + ':' : '' }}
                {{ basePrice ? basePrice : price }}
                </ins>
                <del v-if="discount" class="text-sm md:text-base font-normal text-muted ms-2">
                {{ price }}
                </del>
            </span>
        </template>
        <VariantShare v-if="position === 'inside'" :variant="variant" />
    </div>
    <div v-if="showAuctionWinnerInfo" class="w-full mt-5">
      <AuctionSelectWinner v-if="auth.idSupplier === variant?.product?.supplier?.id" :variant="variant" :auction="variant.auction" :bids="bids" class="w-full mb-8" />
      <AuctionWinner :auction="variant.auction" class="w-full mb-8" />
      <AuctionCurrentWinner :auction="variant.auction" :bids="bids" class="w-full mb-8" />
    </div>
  </div>
</template>

<script setup>
import * as Sentry from "@sentry/vue";
import _ from 'lodash-es';
import { PRODUCT_TYPE, STATUS } from "@utils/constants";
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { useAuthStore } from '@stores/auth';
import usePrice from "@utils/use-price";
import AuctionWinner from "@components/auction/auction-winner";
import AuctionCurrentWinner from "@components/auction/auction-current-winner";
import AuctionSelectWinner from "@components/auction/auction-select-winner";
import VariantShare from "@components/product/product-details/variant-share";

const props = defineProps({
  variant: {
    type: Object,
    required: true
  },
  position: {
    type: String,
    default: 'inside' //inside, outside
  }
})

const auth = useAuthStore();
const { t } = useI18n();
const { $dayjs, $eventBus, $toast } = useNuxtApp();
const now = useNow();
const bids = ref({ items: [] });

const isAuction = computed(() => props.variant?.product?.type === PRODUCT_TYPE.AUCTION && !!props.variant?.auction?.id);
const showAuctionWinnerInfo = computed(() => isAuction.value && props.position === 'outside');

const auctionPrice = computed(() => _.get(props.variant, 'auction.currentBid', null) || _.get(props.variant, 'auction.startingBid', null));
const auctionFinished = computed(() => props.variant?.auction?.finishAt && $dayjs(props.variant.auction.finishAt).isBefore(now.value));
const auctionHasBids = computed(() => _.get(props.variant, 'auction.bidCount', 0) > 0 || _.get(props.variant, 'auction.currentBid', null) !== null || (bids.value?.items || []).length > 0);
const auctionPriceLabel = computed(() => {
  if (!auctionPrice.value) {
    return '';
  }

  if (!auctionFinished.value) {
    return 'text-current-price';
  }

  return auctionHasBids.value ? 'text-winning-price' : 'text-final-price';
});

const price = computed(() => usePrice({
  amount: auctionPrice.value || +props.variant?.price,
  baseAmount: auctionPrice.value || +props.variant?.sale_price,
  currencyCode: props.variant?.currency?.code
})?.price);

const basePrice = computed(() => usePrice({
  amount: auctionPrice.value || +props.variant?.price,
  baseAmount: auctionPrice.value || +props.variant?.sale_price,
  currencyCode: props.variant?.currency?.code
})?.basePrice);

const discount = computed(() => usePrice({
  amount: auctionPrice.value || +props.variant?.price,
  baseAmount: auctionPrice.value || +props.variant?.sale_price,
  currencyCode: props.variant?.currency?.code
})?.discount);

const isAnonymized = computed(() => _.includes([STATUS.HIDDEN, STATUS.CLOSED], props.variant?.product?.status));

const addBid = (data) => {
  if (!_.find(bids.value?.items || [], { id: data.id })) {
    if (auth.idCustomer === data.customer.id) {
      data.insertedAt = null;
    }
    bids.value.items.unshift(data);
  }
}

const reload = async () => {
  if (!showAuctionWinnerInfo.value) {
    return;
  }

  try {
    const data = await $apiFetch(API_ENDPOINTS.ESHOP_PRODUCT_VARIANT_AUCTION_BID, {
      method: 'GET',
      path: { idAuction: props.variant?.auction?.id },
      params: {
        page: 1,
        limit: 15
      }
    });
    bids.value.items = data?.items || [];
  } catch (error) {
    if (import.meta.client) {
      $toast.error(t('error.auction-bid.load'));
    }
    Sentry.captureException(error);
  }
}

const auctionBidAdd = (data) => {
  if (data.id === props.variant?.auction?.id) {
    addBid(data.bid);
  }
};

onMounted(async () => {
  if (!showAuctionWinnerInfo.value) {
    return;
  }

  if (import.meta.server || navigator?.onLine) {
    await reload();
  }
  $eventBus.on('auction:bid:add', auctionBidAdd);
});

onUnmounted(() => {
  $eventBus.off('auction:bid:add', auctionBidAdd);
});

</script>
